import { useEffect, useState } from 'react';
import { fetchPackageAvailableDates } from '../api/packages.api';
import type { AvailableDates } from '../../shared/availableMonths';

export interface UsePackageAvailableDatesResult {
  availableDates: AvailableDates | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Departure months offered for an origin/destination pair. Both ids are the
 * comma-joined form the API expects (`12,34`); passing an empty one clears.
 */
export function usePackageAvailableDates(
  originId: string,
  destinationId: string,
): UsePackageAvailableDatesResult {
  const [availableDates, setAvailableDates] = useState<AvailableDates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!originId || !destinationId) {
      setAvailableDates(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setIsLoading(true);
    setError(null);

    fetchPackageAvailableDates(originId, destinationId, controller.signal)
      .then((result) => {
        if (!active) return;
        setAvailableDates(result);
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return;
        setAvailableDates(null);
        setError(cause instanceof Error ? cause.message : 'packages.api.requestFailed');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [originId, destinationId]);

  return { availableDates, isLoading, error };
}
